/* The two reachable ways an invitation gets sent.
 *
 * AccessLinkModal — staff, from a Suivi dossier: send the member the link that lets them
 *   register and claim the row the médecin created for them. Also the resend.
 * InviteRelativeModal — a participant inviting someone into their OWN dossier (a parent
 *   declaring the second parent). The role and wording come from the study's invitation
 *   configuration, so this stays study-agnostic: whatever role the researcher allowed a
 *   subject to invite is what the button offers.
 *
 * Both post to the same API. The dossier binding is NOT handled here: the server pins the
 * invitee to the exact member (staff) or to the sender's own numéro (participant), so a
 * mistyped field can never route someone into another family.
 */
import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { IonModal } from "@ionic/react";
import {
  ModalCard,
  ModalTitle,
  ModalDescription,
  ModalActions,
  ModalPrimaryButton,
  ModalQuietButton,
  ModalError,
  ModalFields,
} from "../../atoms/ModalKit";
import { TextField } from "../../atoms/LoginTextField";
import { useConfig } from "../../../contexts/ConfigContext";
import { getInvitations, sendInvitation } from "../../../services/invitation.service";
import {
  InvitationChannelEnum,
  InvitationConfigType,
} from "../../../types/invitation.types";

interface AccessLinkModalProps {
  isOpen: boolean;
  memberName: string;
  /** Already on the member's fiche — when absent the médecin must supply one. */
  knownEmail: string | null;
  alreadySentAt: string | null;
  saving: boolean;
  error: string | null;
  onDismiss: () => void;
  onConfirm: (payload: { email: string | null }) => void;
}

export const AccessLinkModal = ({
  isOpen,
  memberName,
  knownEmail,
  alreadySentAt,
  saving,
  error,
  onDismiss,
  onConfirm,
}: AccessLinkModalProps): ReactElement => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");

  // Reset between members: the modal instance is reused across rows.
  useEffect(() => {
    if (isOpen) setEmail("");
  }, [isOpen]);

  return (
    <IonModal isOpen={isOpen} className="resonance-modal" backdropDismiss onWillDismiss={onDismiss}>
      <ModalCard>
        <ModalTitle>
          {alreadySentAt ? t("suivi_access_link_resend_title") : t("suivi_access_link_title")}
        </ModalTitle>
        <ModalDescription>
          {t("suivi_access_link_text", { name: memberName })}
        </ModalDescription>

        {knownEmail ? (
          <KnownContact>{knownEmail}</KnownContact>
        ) : (
          <ModalFields>
            <TextField
              type="email"
              onChange={(text) => setEmail(text)}
              value={email}
              placeholder={t("suivi_access_link_email_label")}
              label={t("suivi_access_link_email_label")}
            />
          </ModalFields>
        )}

        {error && <ModalError>{error}</ModalError>}

        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
          <ModalPrimaryButton
            disabled={saving || (!knownEmail && !email.trim())}
            onClick={() => onConfirm({ email: knownEmail ? null : email.trim() })}
          >
            {t("suivi_access_link_confirm")}
          </ModalPrimaryButton>
        </ModalActions>
      </ModalCard>
    </IonModal>
  );
};

interface InviteRelativeModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSent: () => void;
}

export const InviteRelativeModal = ({
  isOpen,
  onDismiss,
  onSent,
}: InviteRelativeModalProps): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { study } = useConfig();

  const [configs, setConfigs] = useState<InvitationConfigType[]>([]);
  const [configId, setConfigId] = useState<number | null>(null);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Email only: an invitation the participant sends must land somewhere durable, and the
  // SMS body column cannot hold a full message plus a link.
  useEffect(() => {
    if (!isOpen || !study?.id) return;
    let cancelled = false;
    getInvitations({ studyId: study.id, lang: language })
      .then(({ data }) => {
        if (cancelled) return;
        const emailConfigs = (data as InvitationConfigType[]).filter(
          (config) =>
            config.channel === InvitationChannelEnum.EMAIL && !config.to_role_already_joined
        );
        setConfigs(emailConfigs);
        if (emailConfigs.length === 1) setConfigId(emailConfigs[0].id);
      })
      .catch(() => {
        /* fail-soft: no configs → the empty state below explains it */
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, study?.id, language]);

  const handleSend = async (): Promise<void> => {
    if (!configId) {
      setError(t("invite_relative_role_required"));
      return;
    }
    if (!firstname.trim() || !lastname.trim() || !email.trim()) {
      setError(t("invite_relative_fields_required"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await sendInvitation(
        { study_id: study.id },
        {
          target_user: {
            firstname: firstname.trim(),
            lastname: lastname.trim(),
            email: email.trim(),
          },
          channel: InvitationChannelEnum.EMAIL,
          invitation_config_id: configId,
          lang: language,
        } as never
      );
      setFirstname("");
      setLastname("");
      setEmail("");
      onSent();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? t("common_server_error_desc"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <IonModal isOpen={isOpen} className="resonance-modal" backdropDismiss onWillDismiss={onDismiss}>
      <ModalCard>
        <ModalTitle>{t("invite_relative_title")}</ModalTitle>
        <ModalDescription>{t("invite_relative_text")}</ModalDescription>

        {configs.length === 0 ? (
          <ModalError>{t("invite_relative_unavailable")}</ModalError>
        ) : (
          <>
            {/* Only when the study lets a participant invite more than one kind of
                person — otherwise the single role is implied and asking is noise. */}
            {configs.length > 1 && (
              <Choices>
                {configs.map((config) => (
                  <Choice
                    key={config.id}
                    type="button"
                    $selected={configId === config.id}
                    aria-pressed={configId === config.id}
                    onClick={() => setConfigId(config.id)}
                  >
                    {config.to_role_label ?? config.to_role}
                  </Choice>
                ))}
              </Choices>
            )}

            <ModalFields>
              <TextField
                type="text"
                onChange={(text) => setFirstname(text)}
                value={firstname}
                placeholder={t("invite_relative_firstname")}
                label={t("invite_relative_firstname")}
              />
              <TextField
                type="text"
                onChange={(text) => setLastname(text)}
                value={lastname}
                placeholder={t("invite_relative_lastname")}
                label={t("invite_relative_lastname")}
              />
              <TextField
                type="email"
                onChange={(text) => setEmail(text)}
                value={email}
                placeholder={t("invite_relative_email")}
                label={t("invite_relative_email")}
              />
            </ModalFields>
          </>
        )}

        {error && <ModalError>{error}</ModalError>}

        <ModalActions>
          <ModalQuietButton onClick={onDismiss}>{t("common_cancel")}</ModalQuietButton>
          {configs.length > 0 && (
            <ModalPrimaryButton onClick={handleSend} disabled={saving}>
              {t("invite_relative_confirm")}
            </ModalPrimaryButton>
          )}
        </ModalActions>
      </ModalCard>
    </IonModal>
  );
};

const KnownContact = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.05);
  font-size: 15px;
  color: var(--app-color-text, #0f172a);
  word-break: break-all;
`;

const Choices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
`;

const Choice = styled.button<{ $selected: boolean }>`
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 14px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  border: 1px solid
    ${({ $selected }) =>
      $selected
        ? "var(--app-color-primary, #0f766e)"
        : "rgba(var(--app-color-text-rgb, 15, 23, 42), 0.15)"};
  background: ${({ $selected }) =>
    $selected ? "rgba(var(--app-color-primary-rgb, 15, 118, 110), 0.08)" : "transparent"};
  color: var(--app-color-text, #0f172a);
`;
